import React, {useEffect, useState} from 'react';
import heic2any from 'heic2any';
import { useNavigate, useLocation } from 'react-router-dom';
import { useApi, Requirement } from '../contexts/ApiContext';
import Button from '../components/ui/Button';
import Alert from '../components/ui/Alert';
import {useAlert} from "../contexts/AlertContext";
import DatePicker from 'react-datepicker';
import 'react-datepicker/dist/react-datepicker.css';

interface UploadedFileInfo {
    file_id?: string;
    file_object_key: string;
    filename: string;
    content_type: string;
    size: number;
}

const RequirementDetailPage: React.FC = () => {
    const navigate = useNavigate();
    const location = useLocation();
    const { submitRequirement, fetchRequirements } = useApi();
    const initialRequirement = location.state?.requirement as Requirement;
    const { showAlert, clearAllAlerts } = useAlert();

    // Form state
    const [uploadedFiles, setUploadedFiles] = useState<File[]>([]);
    const [existingFiles, setExistingFiles] = useState<UploadedFileInfo[]>([]);
    const [deletedFileIds, setDeletedFileIds] = useState<string[]>([]);
    const [issuedDate, setIssuedDate] = useState<Date | null>(null);
    const [expiryDate, setExpiryDate] = useState<Date | null>(null);
    const [customFieldValues, setCustomFieldValues] = useState<{ [key: string]: string }>({});
    const [customFieldOtherValues, setCustomFieldOtherValues] = useState<{ [key: string]: string }>({});
    const [customFieldDates, setCustomFieldDates] = useState<{ [key: string]: Date | null }>({});
    const [tickBoxStates, setTickBoxStates] = useState<{ [key: string]: boolean }>({});
    const [loading, setLoading] = useState(false);
    const [loadingRequirement, setLoadingRequirement] = useState(true);
    const [clientRequirementId, setClientRequirementId] = useState<string | null>(null);
    const [isExempt, setIsExempt] = useState(false);
    const [exemptReason, setExemptReason] = useState('');
    const [hasLoadedFiles, setHasLoadedFiles] = useState(false);

    // Use fullRequirement if loaded, otherwise use initialRequirement
    const [fullRequirement, setFullRequirement] = useState<Requirement | null>(null);
    const requirement = fullRequirement || initialRequirement;

    // Load requirement data from API on mount
    useEffect(() => {
        // Don't load if no requirement
        if (!initialRequirement) return;

        console.log('Initial requirement:', initialRequirement);

        const loadRequirementData = async () => {
            setLoadingRequirement(true);

            const foundRequirement = initialRequirement;

            console.log('Found requirement:', foundRequirement);
            console.log('Form fields:', foundRequirement.form_fields);
            console.log('i_am_exempt field:', foundRequirement.form_fields.i_am_exempt);

            setClientRequirementId(foundRequirement.client_requirement_id);
            setFullRequirement(foundRequirement);

            // Populate i_am_exempt if it exists
            if (foundRequirement.form_fields.i_am_exempt?.value) {
                const exemptValue = foundRequirement.form_fields.i_am_exempt.value;
                setIsExempt(exemptValue === true || exemptValue === 'true');
            }

            // Populate exempt_reason if it exists
            if (foundRequirement.form_fields.i_am_exempt?.exempt_reason) {
                setExemptReason(foundRequirement.form_fields.i_am_exempt.exempt_reason);
            }

            // Populate issued date if it exists
            if (foundRequirement.form_fields.issued_date?.value) {
                const [year, month, day] = foundRequirement.form_fields.issued_date.value.split('-');
                setIssuedDate(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)));
            }

            // Populate expiry date if it exists
            if (foundRequirement.form_fields.expiry_date?.value) {
                const [year, month, day] = foundRequirement.form_fields.expiry_date.value.split('-');
                setExpiryDate(new Date(parseInt(year), parseInt(month) - 1, parseInt(day)));
            }

            // Populate existing uploaded files if they exist (only on first load)
            if (!hasLoadedFiles && foundRequirement.form_fields.upload_document?.value) {
                setExistingFiles(foundRequirement.form_fields.upload_document.value);
                setHasLoadedFiles(true);
            }

            // Populate custom form field values
            const customValues: { [key: string]: string } = {};
            const customDates: { [key: string]: Date | null } = {};
            const customOtherValues: { [key: string]: string } = {};

            foundRequirement.custom_form_fields.forEach(field => {
                if (!field.value) return;

                if (field.type === 'date_picker') {
                    // Parse YYYY-MM-DD into a Date object for the DatePicker control
                    const parts = field.value.split('-');
                    if (parts.length === 3) {
                        customDates[field.id] = new Date(parseInt(parts[0]), parseInt(parts[1]) - 1, parseInt(parts[2]));
                        customValues[field.id] = field.value;
                    }
                } else if (field.type === 'dropdown_select' && field.include_other) {
                    // If the saved value isn't one of the known options it was an "Other" answer
                    if (field.options && !field.options.includes(field.value)) {
                        customValues[field.id] = 'Other';
                        customOtherValues[field.id] = field.value;
                    } else {
                        customValues[field.id] = field.value;
                    }
                } else {
                    customValues[field.id] = field.value;
                }
            });

            setCustomFieldValues(customValues);
            setCustomFieldDates(customDates);
            setCustomFieldOtherValues(customOtherValues);

            // Populate tick box states
            const tickStates: { [key: string]: boolean } = {};
            foundRequirement.acknowledgement_tick_boxes.forEach(tickBox => {
                const key = `${tickBox.id}`;
                tickStates[key] = tickBox.checked || false;
            });
            setTickBoxStates(tickStates);

            setLoadingRequirement(false);
        };

        loadRequirementData();
    }, [initialRequirement, fetchRequirements]);

    // Redirect if no requirement data
    if (!initialRequirement) {
        navigate('/requirements');
        return null;
    }

    // If still loading, show the initial requirement structure
    if (!requirement) {
        return <div>Loading...</div>;
    }

    const isImageFile = (file: File): boolean => {
        const imageTypes = ['image/jpeg', 'image/jpg', 'image/png', 'image/gif', 'image/bmp', 'image/webp', 'image/tiff', 'image/heic', 'image/heif'];
        return imageTypes.includes(file.type.toLowerCase());
    };

    const resizeImage = async (file: File, maxSizeInBytes: number): Promise<File> => {
        // Convert HEIC/HEIF to JPEG first, since browsers can't decode them natively
        const isHeic = file.type === 'image/heic' || file.type === 'image/heif'
            || file.name.toLowerCase().endsWith('.heic') || file.name.toLowerCase().endsWith('.heif');

        if (isHeic) {
            const converted = await heic2any({ blob: file, toType: 'image/jpeg', quality: 0.9 });
            const jpegBlob = Array.isArray(converted) ? converted[0] : converted;
            const jpegName = file.name.replace(/\.(heic|heif)$/i, '.jpg');
            file = new File([jpegBlob], jpegName, { type: 'image/jpeg', lastModified: Date.now() });
            if (file.size <= maxSizeInBytes) return file;
        }

        return new Promise((resolve, reject) => {
            const reader = new FileReader();

            reader.onload = (e) => {
                const img = new Image();

                img.onload = () => {
                    const canvas = window.document.createElement('canvas');
                    const ctx = canvas.getContext('2d');

                    if (!ctx) {
                        reject(new Error('Could not get canvas context'));
                        return;
                    }

                    // Start with original dimensions
                    let width = img.width;
                    let height = img.height;

                    // Calculate scaling factor to reduce file size
                    // Start with 0.9 quality and reduce dimensions if needed
                    let quality = 0.9;
                    let scaleFactor = 1.0;

                    // If image is very large, reduce dimensions first
                    const maxDimension = 2048;
                    if (width > maxDimension || height > maxDimension) {
                        if (width > height) {
                            scaleFactor = maxDimension / width;
                        } else {
                            scaleFactor = maxDimension / height;
                        }
                        width = Math.floor(width * scaleFactor);
                        height = Math.floor(height * scaleFactor);
                    }

                    canvas.width = width;
                    canvas.height = height;
                    ctx.drawImage(img, 0, 0, width, height);

                    // Try to compress with reducing quality
                    const tryCompress = (currentQuality: number, currentScale: number) => {
                        const tempWidth = Math.floor(img.width * currentScale);
                        const tempHeight = Math.floor(img.height * currentScale);

                        canvas.width = tempWidth;
                        canvas.height = tempHeight;
                        ctx.drawImage(img, 0, 0, tempWidth, tempHeight);

                        canvas.toBlob(
                            (blob) => {
                                if (!blob) {
                                    reject(new Error('Could not compress image'));
                                    return;
                                }

                                // If still too large and we can reduce further
                                if (blob.size > maxSizeInBytes && (currentQuality > 0.3 || currentScale > 0.3)) {
                                    const newQuality = Math.max(0.3, currentQuality - 0.1);
                                    const newScale = currentQuality <= 0.3 ? Math.max(0.3, currentScale - 0.1) : currentScale;
                                    tryCompress(newQuality, newScale);
                                } else {
                                    // Create new file from blob
                                    const compressedFile = new File([blob], file.name, {
                                        type: 'image/jpeg',
                                        lastModified: Date.now()
                                    });
                                    resolve(compressedFile);
                                }
                            },
                            'image/jpeg',
                            currentQuality
                        );
                    };

                    tryCompress(quality, scaleFactor);
                };

                img.onerror = () => {
                    reject(new Error('Could not load image'));
                };

                img.src = e.target?.result as string;
            };

            reader.onerror = () => {
                reject(new Error('Could not read file'));
            };

            reader.readAsDataURL(file);
        });
    };

    const handleFileUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
        if (e.target.files) {
            const newFiles = Array.from(e.target.files);
            const maxSizeInBytes = 5 * 1024 * 1024; // 5 MB

            const validFiles: File[] = [];
            const oversizedFiles: string[] = [];
            const resizedFiles: string[] = [];

            for (const file of newFiles) {
                if (file.size > maxSizeInBytes) {
                    // If it's an image, try to resize it
                    if (isImageFile(file)) {
                        try {
                            const resizedFile = await resizeImage(file, maxSizeInBytes);
                            if (resizedFile.size <= maxSizeInBytes) {
                                validFiles.push(resizedFile);
                                resizedFiles.push(file.name);
                            } else {
                                oversizedFiles.push(file.name);
                            }
                        } catch (error) {
                            console.error('Error resizing image:', error);
                            oversizedFiles.push(file.name);
                        }
                    } else {
                        // Non-image files that are too large
                        oversizedFiles.push(file.name);
                    }
                } else {
                    validFiles.push(file);
                }
            }

            // Show alert if any images were resized
            if (resizedFiles.length > 0) {
                const fileList = resizedFiles.join(', ');
                showAlert(
                    `The following image(s) were automatically compressed to meet the 5 MB size limit: ${fileList}`,
                    'info'
                );
            }

            // Show alert if any files were too large
            if (oversizedFiles.length > 0) {
                const fileList = oversizedFiles.join(', ');
                showAlert(
                    `The following file(s) exceed the 5 MB size limit and could not be added: ${fileList}`,
                    'error',
                    5000,
                    undefined,
                    true  // persistent – stays until user clicks Browse again or dismisses with ×
                );
            }

            // Add only valid files
            if (validFiles.length > 0) {
                setUploadedFiles([...uploadedFiles, ...validFiles]);
            }

            // Reset the input value to allow re-uploading the same file
            e.target.value = '';
        }
    };

    const handleRemoveFile = (index: number) => {
        setUploadedFiles(uploadedFiles.filter((_, i) => i !== index));
    };

    const handleRemoveExistingFile = (index: number) => {
        const fileToRemove = existingFiles[index];
        const confirmed = window.confirm(
            `Are you sure you want to delete "${fileToRemove.filename}"?\n\n` +
            'This file will be removed when you submit the requirement.'
        );

        if (confirmed) {
            // Track the file_id for deletion
            if (fileToRemove.file_id) {
                setDeletedFileIds([...deletedFileIds, fileToRemove.file_id]);
            }
            // Remove from existing files list
            setExistingFiles(existingFiles.filter((_, i) => i !== index));
        }
    };

    const handleCustomFieldChange = (fieldId: string, value: string) => {
        setCustomFieldValues(prev => ({ ...prev, [fieldId]: value }));
    };

    const handleCustomFieldOtherChange = (fieldId: string, value: string) => {
        setCustomFieldOtherValues(prev => ({ ...prev, [fieldId]: value }));
    };

    const handleCustomDateChange = (fieldId: string, date: Date | null) => {
        setCustomFieldDates(prev => ({ ...prev, [fieldId]: date }));
        if (date) {
            const year = date.getFullYear();
            const month = String(date.getMonth() + 1).padStart(2, '0');
            const day = String(date.getDate()).padStart(2, '0');
            setCustomFieldValues(prev => ({ ...prev, [fieldId]: `${year}-${month}-${day}` }));
        } else {
            setCustomFieldValues(prev => ({ ...prev, [fieldId]: '' }));
        }
    };

    const handleExemptCheckboxChange = (checked: boolean) => {
        setIsExempt(checked);
    };

    // Calculate expiration date based on duration and unit
    const calculateExpiresInDate = (duration: number, unit: string): string => {
        const currentDate = new Date();

        switch (unit.toLowerCase()) {
            case 'days':
            case 'day':
                currentDate.setDate(currentDate.getDate() + duration);
                break;
            case 'weeks':
            case 'week':
                currentDate.setDate(currentDate.getDate() + (duration * 7));
                break;
            case 'months':
            case 'month':
                currentDate.setMonth(currentDate.getMonth() + duration);
                break;
            case 'years':
            case 'year':
                currentDate.setFullYear(currentDate.getFullYear() + duration);
                break;
            default:
                console.warn(`Unknown unit: ${unit}, defaulting to days`);
                currentDate.setDate(currentDate.getDate() + duration);
        }

        // Format as YYYY-MM-DD
        const year = currentDate.getFullYear();
        const month = String(currentDate.getMonth() + 1).padStart(2, '0');
        const day = String(currentDate.getDate()).padStart(2, '0');

        return `${year}-${month}-${day}`;
    };

    const handleTickBoxChange = (tickBoxId: number) => {
        const key = `${tickBoxId}`;
        setTickBoxStates({
            ...tickBoxStates,
            [key]: !tickBoxStates[key]
        });
    };

    const scrollToElement = (elementId: string) => {
        const element = window.document.getElementById(elementId);
        if (element) {
            element.scrollIntoView({
                behavior: 'smooth',
                block: 'center'
            });
            // Add a brief highlight effect
            element.focus();
        }
    };

    const validateForm = (): boolean => {
        // Validate upload_document if required (skip when exempt)
        if (!isExempt &&
            requirement.form_fields.upload_document?.enabled &&
            requirement.form_fields.upload_document?.required &&
            uploadedFiles.length === 0 && existingFiles.length === 0) {
            console.log('Please upload at least one document');
            showAlert('Please upload at least one document', 'error');
            scrollToElement('upload_document');
            return false;
        }

        // If exempt, validate exempt reason (always required when exempt)
        if (isExempt) {
            if (!exemptReason.trim()) {
                showAlert('Please provide a reason for exemption', 'error');
                scrollToElement('exempt_reason');
                return false;
            }
            return true;
        }

        // Validate issued_date if required (only when not exempt)
        if (requirement.form_fields.issued_date?.enabled &&
            requirement.form_fields.issued_date?.required &&
            !issuedDate) {
            showAlert('Please enter an issued date', 'error');
            scrollToElement('issued_date');
            return false;
        }

        // Validate expiry_date if required (only when not exempt)
        if (requirement.form_fields.expiry_date?.enabled &&
            requirement.form_fields.expiry_date?.required &&
            !expiryDate) {
            showAlert('Please enter an expiry date', 'error');
            scrollToElement('expiry_date');
            return false;
        }

        // Validate custom form fields (only when not exempt)
        for (const field of requirement.custom_form_fields) {
            if (!field.enabled) continue;

            if (field.required && !customFieldValues[field.id]) {
                showAlert(`Please fill in ${field.name}`, 'error');
                scrollToElement(field.id);
                return false;
            }

            // For dropdown_select with "Other" selected, the free-text input is also required
            if (field.type === 'dropdown_select' && field.include_other &&
                customFieldValues[field.id] === 'Other' &&
                !customFieldOtherValues[field.id]?.trim()) {
                showAlert(`Please specify your answer for "${field.name}"`, 'error');
                scrollToElement(`${field.id}_other`);
                return false;
            }

            // For date_picker, ensure the stored value is a valid date string
            if (field.type === 'date_picker' && field.required && !customFieldDates[field.id]) {
                showAlert(`Please enter a valid date for "${field.name}"`, 'error');
                scrollToElement(field.id);
                return false;
            }
        }

        // Validate required tick boxes (only when not exempt)
        for (const tickBox of requirement.acknowledgement_tick_boxes) {
            if (tickBox.required) {
                const key = `${tickBox.id}`;
                if (!tickBoxStates[key]) {
                    showAlert(`Please acknowledge: ${tickBox.text}`, 'error');
                    scrollToElement(`checkbox-${key}`);
                    return false;
                }
            }
        }

        return true;
    };

    const handleSubmit = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!validateForm()) {
            return;
        }

        // // Check if this is a resubmission (client_requirement_id exists and is not "NEW")
        // if (clientRequirementId && clientRequirementId !== 'NEW') {
        //     const confirmed = window.confirm(
        //         'This requirement has been previously approved.\n\n' +
        //         'By resubmitting, this requirement will need to go through the appropriate approval processes again.\n\n' +
        //         'Do you want to continue?'
        //     );
        //
        //     if (!confirmed) {
        //         return; // User cancelled the resubmission
        //     }
        // }

        setLoading(true);

        // Build FormData
        const formData = new FormData();

        if (clientRequirementId !== 'NEW') {
            formData.append('client_requirement_id', clientRequirementId || '');
        }
        formData.append('customer_code', sessionStorage.getItem('customerCode') || '');
        formData.append('requirement_id', requirement.requirement_id);
        formData.append('client_type', requirement.client_type);
        // Resolve dropdown_select "Other" entries to their actual free-text value before submitting
        const customFieldsForSubmission: { [key: string]: string } = {};
        for (const field of requirement.custom_form_fields) {
            if (field.type === 'dropdown_select' && field.include_other &&
                customFieldValues[field.id] === 'Other') {
                customFieldsForSubmission[field.id] = customFieldOtherValues[field.id] || '';
            } else {
                customFieldsForSubmission[field.id] = customFieldValues[field.id] || '';
            }
        }
        formData.append('custom_form_fields', JSON.stringify(customFieldsForSubmission));
        formData.append('acknowledgement_tick_boxes', JSON.stringify(tickBoxStates));

        // Add i_am_exempt status and reason
        formData.append('i_am_exempt', isExempt.toString());
        if (isExempt) {
            formData.append('exempt_reason', exemptReason);
        }

        // Add existing files information (if any)
        if (existingFiles.length > 0) {
            formData.append('existing_files', JSON.stringify(existingFiles));
        }

        // Add deleted file IDs (if any)
        if (deletedFileIds.length > 0) {
            formData.append('deleted_file_ids', JSON.stringify(deletedFileIds));
            console.log('Deleted file IDs:', deletedFileIds);
        }

        // Add new files to upload
        uploadedFiles.forEach((file, index) => {
            formData.append(`file_${index}`, file);
        });

        // Convert Date objects to yyyy-mm-dd strings for API
        let issuedDateString = '';
        if (issuedDate) {
            const year = issuedDate.getFullYear();
            const month = String(issuedDate.getMonth() + 1).padStart(2, '0');
            const day = String(issuedDate.getDate()).padStart(2, '0');
            issuedDateString = `${year}-${month}-${day}`;
        }

        let expiryDateString = '';
        if (expiryDate) {
            const year = expiryDate.getFullYear();
            const month = String(expiryDate.getMonth() + 1).padStart(2, '0');
            const day = String(expiryDate.getDate()).padStart(2, '0');
            expiryDateString = `${year}-${month}-${day}`;
        }

        formData.append('issued_date', issuedDateString);
        formData.append('expiry_date', expiryDateString);

        // Calculate and add expires_in date if enabled
        if ((clientRequirementId === 'NEW' || requirement.status === 'sent' || requirement.status === 'to_be_sent') &&
            requirement.form_fields.expires_in?.enabled &&
            requirement.form_fields.expires_in.duration &&
            requirement.form_fields.expires_in.unit) {
            const expiresInDate = calculateExpiresInDate(
                requirement.form_fields.expires_in.duration,
                requirement.form_fields.expires_in.unit
            );
            formData.append('expires_in', expiresInDate);
        }

        console.log('Form data to submit:', formData);

        const success = await submitRequirement(requirement.requirement_id, formData, requirement.client_type);

        setLoading(false);

        if (success) {
            switch (requirement.client_type) {
                case 'company':
                    setTimeout(() => navigate('/company-requirements'), 1500);
                    break;
                default:
                    setTimeout(() => navigate('/requirements'), 1500);
            }
        }
    };

    const handleGoBack = () => {
        navigate(-1); // Navigate back to the previous page
    };

    if (loadingRequirement) {
        return (
            <div className="container">
                <div className="header">
                    <h1>📄 {requirement.requirement_name}</h1>
                    <p>Loading requirement details...</p>
                </div>
                <div style={{ textAlign: 'center', padding: '40px' }}>
                    <div className="loading-spinner" style={{ margin: '0 auto' }}></div>
                </div>
            </div>
        );
    }

    return (
        <div className="container">
            <div className="header">
                <h1>📄 {requirement.requirement_name}</h1>
                <p>{requirement.description}</p>
            </div>

            <div className="form-container">
                <Alert />
                <form onSubmit={handleSubmit} className="requirement-detail-form" autoComplete="off" data-lpignore="true">
                    {/* Requirement Info - Display as formatted text */}
                    {/*<div className="requirement-info-section">*/}
                    {/*    <div className="requirement-info-display">*/}
                    {/*        <h2 className="requirement-title">📄 {requirement.requirement_name}</h2>*/}
                    {/*        <p className="requirement-description">{requirement.description}</p>*/}
                    {/*    </div>*/}
                    {/*</div>*/}

                    {/* Upload Requirement Field */}
                    {requirement.form_fields.upload_document?.enabled && (
                        <div className="form-group">
                            <label htmlFor="upload_document">
                                {requirement.form_fields.upload_document.display_name}
                                {requirement.form_fields.upload_document.required && !isExempt && (
                                    <span style={{ color: 'red' }}> *</span>
                                )}
                            </label>
                            <input
                                type="file"
                                id="upload_document"
                                onClick={clearAllAlerts}
                                onChange={handleFileUpload}
                                multiple
                                className="file-input"
                                accept=".pdf,.doc,.docx,.txt,.rtf,.odt,.xls,.xlsx,.csv,.ppt,.pptx,.jpg,.jpeg,.png,.gif,.bmp,.webp,.svg,.tiff,.tif,.heic,.heif"
                            />
                            <small style={{ color: '#666', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                                Maximum file size: 5 MB per file
                            </small>

                            {/* Show existing files from API */}
                            {existingFiles.length > 0 && (
                                <div className="uploaded-files-list">
                                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>Existing files:</p>
                                    {existingFiles.map((file, index) => (
                                        <div key={`existing-${index}`} className="uploaded-file-item" style={{ backgroundColor: '#e8f5e9' }}>
                                            <span className="file-name">📄 {file.filename}</span>
                                            <span style={{ fontSize: '12px', color: '#666', marginLeft: '10px' }}>
                                                ({(file.size / 1024).toFixed(1)} KB)
                                            </span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveExistingFile(index)}
                                                className="remove-file-btn"
                                                title="Delete file"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}

                            {/* Show newly uploaded files */}
                            {uploadedFiles.length > 0 && (
                                <div className="uploaded-files-list">
                                    <p style={{ fontSize: '14px', color: '#666', marginBottom: '8px' }}>New files to upload:</p>
                                    {uploadedFiles.map((file, index) => (
                                        <div key={index} className="uploaded-file-item">
                                            <span className="file-name">📎 {file.name}</span>
                                            <button
                                                type="button"
                                                onClick={() => handleRemoveFile(index)}
                                                className="remove-file-btn"
                                            >
                                                ✕
                                            </button>
                                        </div>
                                    ))}
                                </div>
                            )}
                        </div>
                    )}

                    {/* I Am Exempt Checkbox - Render from form_fields */}
                    {requirement.form_fields.i_am_exempt?.enabled && (
                        <div className="form-group">
                            <div className="checkbox-group" style={{
                                padding: '15px',
                                backgroundColor: '#f0f8ff',
                                borderRadius: '8px',
                                border: '2px solid #4a90e2'
                            }}>
                                <label className="checkbox-label" style={{ fontSize: '16px', fontWeight: 'bold' }}>
                                    <input
                                        type="checkbox"
                                        id="i_am_exempt"
                                        checked={isExempt}
                                        onChange={(e) => handleExemptCheckboxChange(e.target.checked)}
                                        style={{ marginRight: '10px', transform: 'scale(1.2)' }}
                                    />
                                    <span>{requirement.form_fields.i_am_exempt.display_name}</span>
                                </label>
                            </div>
                        </div>
                    )}

                    {/* Exempt Reason - Show when exempt is checked */}
                    {isExempt && (
                        <div className="form-group" style={{
                            marginTop: '10px',
                            padding: '15px',
                            backgroundColor: '#fff9e6',
                            borderRadius: '8px',
                            border: '2px solid #ffc107'
                        }}>
                            <label htmlFor="exempt_reason">
                                {requirement.form_fields.exempt_reason?.display_name || 'Reason for Exemption'}
                                <span style={{ color: 'red' }}> *</span>
                            </label>
                            <textarea
                                id="exempt_reason"
                                value={exemptReason}
                                onChange={(e) => setExemptReason(e.target.value)}
                                required={true}
                                placeholder="Please provide a detailed reason for your exemption..."
                                rows={4}
                                style={{
                                    width: '100%',
                                    padding: '10px',
                                    fontSize: '14px',
                                    borderRadius: '4px',
                                    border: '1px solid #ccc',
                                    resize: 'vertical',
                                    fontFamily: 'inherit'
                                }}
                            />
                        </div>
                    )}

                    {/* Issued Date Field - Only show if not exempt */}
                    {!isExempt && requirement.form_fields.issued_date?.enabled && (
                        <div className="form-group" data-lpignore="true">
                            <label htmlFor="issued_date">
                                {requirement.form_fields.issued_date.display_name}
                                {requirement.form_fields.issued_date.required && (
                                    <span style={{ color: 'red' }}> *</span>
                                )}
                            </label>
                            <div data-lpignore="true" style={{ position: 'relative' }}>
                                <DatePicker
                                    id="issued_date"
                                    selected={issuedDate}
                                    onChange={(date: Date | null) => setIssuedDate(date)}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="dd/mm/yyyy"
                                    showYearDropdown
                                    showMonthDropdown
                                    dropdownMode="select"
                                    maxDate={new Date()}
                                    yearDropdownItemNumber={100}
                                    scrollableYearDropdown
                                    autoComplete="off"
                                    className="date-picker-input"
                                    wrapperClassName="date-picker-wrapper"
                                />
                            </div>
                            <small style={{ color: '#666', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                                Format: dd/mm/yyyy (e.g., 25/12/1990)
                            </small>
                        </div>
                    )}

                    {/* Expiry Date Field - Only show if not exempt */}
                    {!isExempt && requirement.form_fields.expiry_date?.enabled && (
                        <div className="form-group" data-lpignore="true">
                            <label htmlFor="expiry_date">
                                {requirement.form_fields.expiry_date.display_name}
                                {requirement.form_fields.expiry_date.required && (
                                    <span style={{ color: 'red' }}> *</span>
                                )}
                            </label>
                            <div data-lpignore="true" style={{ position: 'relative' }}>
                                <DatePicker
                                    id="expiry_date"
                                    selected={expiryDate}
                                    onChange={(date: Date | null) => setExpiryDate(date)}
                                    dateFormat="dd/MM/yyyy"
                                    placeholderText="dd/mm/yyyy"
                                    showYearDropdown
                                    showMonthDropdown
                                    dropdownMode="select"
                                    minDate={issuedDate || new Date()}
                                    yearDropdownItemNumber={100}
                                    scrollableYearDropdown
                                    autoComplete="off"
                                    className="date-picker-input"
                                    wrapperClassName="date-picker-wrapper"
                                />
                            </div>
                            {expiryDate && expiryDate < new Date() && (
                                <div style={{
                                    backgroundColor: '#fff3cd',
                                    border: '2px solid #ff9800',
                                    borderRadius: '8px',
                                    padding: '12px 15px',
                                    marginTop: '10px',
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '10px'
                                }}>
                                    <span style={{ fontSize: '24px' }}>⚠️</span>
                                    <div>
                                        <strong style={{ color: '#ff6f00', display: 'block', marginBottom: '4px' }}>
                                            Requirement Expired
                                        </strong>
                                        <span style={{ color: '#856404', fontSize: '0.9em' }}>
                                            This requirement expired on {expiryDate.toLocaleDateString('en-GB')}. Please update with a current expiry date.
                                        </span>
                                    </div>
                                </div>
                            )}
                            {(!expiryDate || expiryDate >= new Date()) && (
                                <small style={{ color: '#666', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                                    Format: dd/mm/yyyy (e.g., 25/12/2025)
                                </small>
                            )}
                        </div>
                    )}

                    {/* Custom Form Fields - Only show if not exempt */}
                    {!isExempt && requirement.custom_form_fields.map((field) => {
                        if (!field.enabled) return null;

                        const renderInput = () => {
                            switch (field.type) {
                                case 'dropdown_select': {
                                    const options = field.options || [];
                                    const selectedValue = customFieldValues[field.id] || '';
                                    return (
                                        <>
                                            <select
                                                id={field.id}
                                                value={selectedValue}
                                                onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                                                required={field.required}
                                            >
                                                <option value="">-- Select an option --</option>
                                                {options.map((opt) => (
                                                    <option key={opt} value={opt}>{opt}</option>
                                                ))}
                                                {field.include_other && (
                                                    <option value="Other">Other – please specify</option>
                                                )}
                                            </select>
                                            {field.include_other && selectedValue === 'Other' && (
                                                <input
                                                    type="text"
                                                    id={`${field.id}_other`}
                                                    value={customFieldOtherValues[field.id] || ''}
                                                    onChange={(e) => handleCustomFieldOtherChange(field.id, e.target.value)}
                                                    required
                                                    placeholder="Please specify..."
                                                    style={{ marginTop: '8px' }}
                                                />
                                            )}
                                        </>
                                    );
                                }
                                case 'date_picker':
                                    return (
                                        <div data-lpignore="true" style={{ position: 'relative' }}>
                                            <DatePicker
                                                id={field.id}
                                                selected={customFieldDates[field.id] || null}
                                                onChange={(date) => handleCustomDateChange(field.id, date)}
                                                dateFormat="dd/MM/yyyy"
                                                placeholderText="dd/mm/yyyy"
                                                showYearDropdown
                                                showMonthDropdown
                                                dropdownMode="select"
                                                maxDate={new Date()}
                                                yearDropdownItemNumber={100}
                                                scrollableYearDropdown
                                                autoComplete="off"
                                                className="date-picker-input"
                                                wrapperClassName="date-picker-wrapper"
                                            />
                                            <small style={{ color: '#666', fontSize: '0.85em', marginTop: '5px', display: 'block' }}>
                                                Format: dd/mm/yyyy (e.g., 25/12/1990)
                                            </small>
                                        </div>
                                    );
                                default: // text
                                    return (
                                        <input
                                            type="text"
                                            id={field.id}
                                            value={customFieldValues[field.id] || ''}
                                            onChange={(e) => handleCustomFieldChange(field.id, e.target.value)}
                                            required={field.required}
                                            placeholder={`Enter ${field.name}`}
                                        />
                                    );
                            }
                        };

                        return (
                            <div key={field.id} className="form-group">
                                <label htmlFor={field.id}>
                                    {field.name}
                                    {field.required && <span style={{ color: 'red' }}> *</span>}
                                </label>
                                {renderInput()}
                            </div>
                        );
                    })}

                    {/* Policies and Agreements - Only show if not exempt */}
                    {!isExempt && requirement.acknowledgement_tick_boxes.length > 0 && (
                        <div className="policies-section">
                            {/* Policy Content */}
                            <div
                                className="policy-content"
                                dangerouslySetInnerHTML={{ __html: requirement.content }}
                            />

                            {/* Acknowledgement Tick Boxes */}
                            {requirement.acknowledgement_tick_boxes.length > 0 && (
                                <div className="acknowledgement-boxes">
                                    {requirement.acknowledgement_tick_boxes.map((tickBox) => {
                                        const key = `${tickBox.id}`;
                                        return (
                                            <div key={key} className="checkbox-group">
                                                <label className="checkbox-label">
                                                    <input
                                                        type="checkbox"
                                                        id={`checkbox-${key}`}
                                                        checked={tickBoxStates[key] || false}
                                                        onChange={() => handleTickBoxChange(tickBox.id)}
                                                        required={tickBox.required}
                                                    />
                                                    <span>
                                                        {tickBox.text}
                                                        {tickBox.required && (
                                                            <span style={{ color: 'red' }}> *</span>
                                                        )}
                                                    </span>
                                                </label>
                                            </div>
                                        );
                                    })}
                                </div>
                            )}
                        </div>
                    )}

                    {/* Action Buttons */}
                    <div className="action-buttons" style={{ marginTop: '30px' }}>
                        <Button type="submit" variant="primary" loading={loading}>
                            Submit Requirement
                        </Button>
                        <Button type="button" variant="secondary" onClick={handleGoBack}>
                            ← Go Back
                        </Button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default RequirementDetailPage;

